import React, { useEffect, useMemo, useState } from 'react';
import { User, Eye, Clock, XCircle, AlertCircle, ChevronLeft, ChevronRight, Search, Calendar, UserCheck, Save, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import AdminFooter from '../components/AdminFooter';
import { useGetApplicationsQuery, useLazyGetApplicationWorkInfoByIdQuery, useUpdateApplicationMutation } from '../store/services/applicationApi';
import { useNavigate } from 'react-router-dom';
import StatusModal from '../components/StatusModal';
import { toast } from 'react-toastify';

const Applications: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [appliedFrom, setAppliedFrom] = useState('');
    const [appliedTo, setAppliedTo] = useState('');
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [application, setApplication] = useState({});
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>([]);

    const navigate = useNavigate();
    const { data: applicationsData, isLoading: isLoadingApplication, refetch } = useGetApplicationsQuery({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: statusFilter,
        appliedFrom,
        appliedTo,
    });
    const [updateApplication] = useUpdateApplicationMutation();
    const [getApplicationWorkInfoById] = useLazyGetApplicationWorkInfoByIdQuery();

    const applicationsState = applicationsData?.data;
    const meta = applicationsData?.data?.meta;
    const visibleApplications = useMemo(
        () => applicationsState?.applications || [],
        [applicationsState?.applications]
    );

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'applied':
                return 'bg-blue-100 text-blue-800';
            case 'scheduled':
                return 'bg-purple-100 text-purple-800';
            case 'selected':
                return 'bg-green-100 text-green-800';
            case 'under-review':
                return 'bg-yellow-100 text-yellow-800';
            case 'submitted':
                return 'bg-indigo-100 text-indigo-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string): string => {
        switch (status) {
            case 'applied':
                return 'Applied';
            case 'scheduled':
                return 'Scheduled';
            case 'selected':
                return 'Selected';
            case 'under-review':
                return 'Under Review';
            case 'submitted':
                return 'Submitted';
            case 'rejected':
                return 'Rejected';
            default:
                return status;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'applied':
                return <Clock className="w-4 h-4" />;
            case 'scheduled':
                return <Calendar className="w-4 h-4" />;
            case 'selected':
                return <UserCheck className="w-4 h-4" />;
            case 'under-review':
                return <AlertCircle className="w-4 h-4" />;
            case 'submitted':
                return <Clock className="w-4 h-4" />;
            case 'rejected':
                return <XCircle className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'Asia/Dhaka',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (limit: number) => {
        setItemsPerPage(limit);
        setCurrentPage(1);
    }

    useEffect(() => {
        setSelectedApplicationIds((currentSelectedIds) =>
            currentSelectedIds.filter((id) => visibleApplications.some((app: any) => app._id === id))
        );
    }, [visibleApplications]);

    const handleUpdateStatus = async (applicationId: string, updateData: { status: string; adminNotes?: string; rejectionReason?: string; remarkText?: string }) => {
        try {
            await updateApplication({
                id: applicationId,
                ...updateData,
            }).unwrap();

            toast.success(`Status updated to "${updateData.status.charAt(0).toUpperCase() + updateData.status.slice(1)}"`);
            await refetch();
        } catch (err: any) {
            toast.error(`Failed to update status: ${err?.data?.message || err.message}`);
        }
    };

    const getPageNumbers = () => {
        const totalPages = meta?.totalPages || 1;
        const currentPageNum = meta?.page || 1;
        const pages = [];

        // Show max 5 page numbers
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPageNum - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const handleSelectApplication = (applicationId: string) => {
        setSelectedApplicationIds((currentSelectedIds) =>
            currentSelectedIds.includes(applicationId)
                ? currentSelectedIds.filter((id) => id !== applicationId)
                : [...currentSelectedIds, applicationId]
        );
    };

    const handleSelectAllApplications = (checked: boolean) => {
        if (checked) {
            setSelectedApplicationIds(visibleApplications.map((app: any) => app._id));
            return;
        }

        setSelectedApplicationIds([]);
    };

    const handleOpenSelectionMode = () => {
        setIsSelectionMode(true);
    };

    const handleCancelSelection = () => {
        setIsSelectionMode(false);
        setSelectedApplicationIds([]);
    };

    const handleSaveData = async () => {
        const selectedApplications = visibleApplications.filter((app: any) =>
            selectedApplicationIds.includes(app._id)
        );

        try {
            const selectedApplicationsWithWorkInfo = await Promise.all(
                selectedApplications.map(async (app: any) => {
                    const workInfo = await getApplicationWorkInfoById(app._id).unwrap();

                    return {
                        ...app,
                        workInfo,
                    };
                })
            );

            console.log('Selected Applications:', selectedApplicationsWithWorkInfo);
            toast.success(`Logged ${selectedApplicationsWithWorkInfo.length} selected application(s)`);
            setIsSelectionMode(false);
            setSelectedApplicationIds([]);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load selected application data');
        }
    };

    const isAllSelected = visibleApplications.length > 0 && selectedApplicationIds.length === visibleApplications.length;

    if (isLoadingApplication) {
        return (
            <div className="min-h-screen bg-[#f2f2f2] flex flex-col">
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex-1 w-full">
                    <div className="bg-white/50 rounded-xl shadow-sm border border-gray-200">
                        <div className="p-4 sm:p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Applications</h3>
                        </div>
                        <div className="p-4 sm:p-6">
                            <div className="animate-pulse space-y-4">
                                {[...Array(5)].map((_, i: any) => (
                                    <div key={i} className="h-16 bg-gray-200 rounded"></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <AdminFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen relative flex flex-col">
            <Header />

            <div className="bg-overlay"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex-1 w-full">
                <div className="bg-white/50 rounded-xl shadow-sm border border-gray-200">
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                            {/* Filters Section */}
                            <div className="w-full space-y-3 sm:flex sm:flex-wrap sm:items-center sm:gap-3 sm:space-y-0">

                                {/* Search Input */}
                                <div className="relative mb-1 w-full sm:mb-0 sm:w-[280px]">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search className="w-4 h-4 text-gray-400" />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search by name, email or phone"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="admin-filter-input pl-9"
                                    />
                                </div>

                                <div className="mt-2 grid grid-cols-2 gap-3 sm:hidden">
                                    {/* Status Dropdown */}
                                    <div className="relative min-w-0">
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => {
                                                setStatusFilter(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="admin-filter-select"
                                        >
                                            <option value="">All Status</option>
                                            <option value="applied">Applied</option>
                                            <option value="scheduled">Scheduled</option>
                                            <option value="selected">Selected</option>
                                            <option value="under-review">Under Review</option>
                                            <option value="submitted">Submitted</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                                    </div>

                                    {/* Date From */}
                                    <input
                                        type="date"
                                        value={appliedFrom}
                                        onChange={(e) => {
                                            setAppliedFrom(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="admin-filter-date min-w-0"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3 sm:hidden">
                                    {/* Date To */}
                                    <input
                                        type="date"
                                        value={appliedTo}
                                        onChange={(e) => {
                                            setAppliedTo(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="admin-filter-date min-w-0"
                                    />

                                    {/* Items Per Page Section */}
                                    <div className="flex min-w-0 items-center justify-end gap-2 sm:justify-start sm:flex-nowrap">
                                        <span className="hidden text-sm text-gray-500 whitespace-nowrap sm:inline">Show</span>
                                        <div className="relative min-w-[68px]">
                                            <select
                                                value={itemsPerPage}
                                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                                className="admin-filter-select min-w-[68px] py-1 pl-3 pr-8"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                                        </div>
                                        <span className="text-sm text-gray-500 whitespace-nowrap">per page</span>
                                    </div>
                                </div>

                                <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-3">
                                    <div className="relative w-[150px]">
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => {
                                                setStatusFilter(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="admin-filter-select"
                                        >
                                            <option value="">All Status</option>
                                            <option value="applied">Applied</option>
                                            <option value="scheduled">Scheduled</option>
                                            <option value="selected">Selected</option>
                                            <option value="under-review">Under Review</option>
                                            <option value="submitted">Submitted</option>
                                            <option value="rejected">Rejected</option>
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                                    </div>

                                    <input
                                        type="date"
                                        value={appliedFrom}
                                        onChange={(e) => {
                                            setAppliedFrom(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="admin-filter-date w-[170px]"
                                    />

                                    <input
                                        type="date"
                                        value={appliedTo}
                                        onChange={(e) => {
                                            setAppliedTo(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="admin-filter-date w-[170px]"
                                    />
                                </div>
                            </div>

                            <div className="hidden items-center gap-2 sm:flex sm:flex-nowrap">
                                <span className="text-sm text-gray-500 whitespace-nowrap">Show</span>
                                <div className="relative">
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                        className="admin-filter-select min-w-[68px] py-1 pl-3 pr-8"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                                </div>
                                <span className="text-sm text-gray-500 whitespace-nowrap">per page</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 sm:p-6">
                        <div className="space-y-4 overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50 border-b border-gray-200">
                                    <tr>
                                        {isSelectionMode && (
                                            <th className="w-16 px-4 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <label className="flex items-center gap-2 text-xs normal-case text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={isAllSelected}
                                                        onChange={(e) => handleSelectAllApplications(e.target.checked)}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span>All</span>
                                                </label>
                                            </th>
                                        )}
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Applicant
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider  md:table-cell">
                                            Phone Number
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider  lg:table-cell">
                                            Date Applied
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white/40 divide-y divide-gray-200">
                                    {visibleApplications.map((app: any) => (
                                        <tr key={app._id} className="hover:bg-gray-50/70 transition duration-200">
                                            {isSelectionMode && (
                                                <td className="w-16 px-4 py-4 whitespace-nowrap text-left align-middle">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedApplicationIds.includes(app._id)}
                                                        onChange={() => handleSelectApplication(app._id)}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                </td>
                                            )}
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="bg-indigo-100 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                                                        {app.profileImage ? (
                                                            <img src={app.profileImage} alt="Applicant" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
                                                        ) : (
                                                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-left text-gray-900 truncate">{app.name}</div>
                                                        <div className="text-sm text-gray-500 text-left truncate">{app.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap md:table-cell">
                                                <div className="text-sm text-gray-900 text-center">{app.phone}</div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap lg:table-cell">
                                                <div className="text-sm text-gray-900 text-center">{formatDateTime(app.submittedDate)}</div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                <div className=" flex justify-center items-center">
                                                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)} flex items-center space-x-1 w-fit`}>
                                                        {getStatusIcon(app.status)}
                                                        <span className=" sm:inline">{getStatusText(app.status)}</span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex flex-col justify-end sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
                                                    <button
                                                        onClick={() => navigate(`/admin/applications/${app._id}`)}
                                                        className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200  p-1 rounded-md text-xs font-medium transition duration-200 flex items-center justify-center space-x-1"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setApplication(app)
                                                            setStatusModalOpen(!statusModalOpen)
                                                        }}
                                                        className={`p-1 rounded-md text-xs font-medium transition duration-200 flex items-center justify-center space-x-1 ${getStatusColor(app.status)}`}
                                                    >
                                                        {getStatusIcon(app.status)}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Component */}
                        {meta && meta.totalPages > 1 && (
                            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                                {/* Results Info */}
                                <div className="text-sm text-gray-500">
                                    Showing {((meta.page - 1) * meta.limit) + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} results
                                </div>

                                {/* Pagination Controls */}
                                <div className="flex items-center space-x-2">
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => handlePageChange(meta.page - 1)}
                                        disabled={!meta.hasPrevPage}
                                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${meta.hasPrevPage
                                            ? 'bg-white/50 text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                            }`}
                                    >
                                        <ChevronLeft className="w-4 h-4 mr-1" />
                                        Prev
                                    </button>

                                    {/* Page Numbers */}
                                    <div className="flex space-x-1">
                                        {getPageNumbers().map((pageNum) => (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${pageNum === meta.page
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-white/50 text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        onClick={() => handlePageChange(meta.page + 1)}
                                        disabled={!meta.hasNextPage}
                                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${meta.hasNextPage
                                            ? 'bg-white/50 text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                            }`}
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Floating Save Data Button */}
            <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 sm:bottom-8 sm:right-8">
                {isSelectionMode && (
                    <button
                        type="button"
                        onClick={handleCancelSelection}
                        className="rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition duration-200 hover:bg-gray-50 sm:px-4"
                    >
                        Cancel
                    </button>
                )}
                <button
                    type="button"
                    onClick={isSelectionMode ? handleSaveData : handleOpenSelectionMode}
                    disabled={isSelectionMode && selectedApplicationIds.length === 0}
                    className="group flex items-center overflow-hidden rounded-full bg-red-500/20 p-2 text-gray-700 backdrop-blur-sm transition-all duration-200 hover:bg-red-500/20 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 sm:hover:px-4"
                >
                    <Save className="w-6 h-6 flex-shrink-0" />
                    <span className="w-0 whitespace-nowrap font-semibold opacity-0 transition-all duration-200 group-hover:ml-2 group-hover:w-auto group-hover:opacity-100 sm:group-hover:ml-2">
                        {isSelectionMode ? 'Save Selected' : 'Save Data'}
                    </span>
                </button>
            </div>

            {/* Status Modal */}
            <StatusModal
                isOpen={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                application={application}
                onUpdateStatus={handleUpdateStatus}
            />
            <AdminFooter />
        </div>
    );
};

export default Applications;
